import React, { useCallback, useEffect, useState } from 'react';

import {
    ActionStatus, GamePlayerStatus,
} from '../../../Constants';
import {
    CARD_NAMES, PIRATES, SEEKERS, VANGUARDS,
} from '../../TwoD/Common/CardUtilities';
import InitialLodgingChoice from
    '../../TwoD/Choices/InitialLodgingChoice';
import {
    playersWithAttackableLodging,
    unUpgradedLodgingIndexes,
} from '../../Game/GamePlayerInfo';

import { ActionAttack } from './ActionAttack';
import { ActionChoice } from './ActionChoice';
import { ActionClockout } from './ActionClockout';
import { ActionInventory } from './ActionInventory';
import { ActionWrapper } from './ActionWrapper';

import './LodgingActionWindow.css';

// Attack-event glyph shown in the defend swiper, per attacking
// faction. Mirrors TwoD AntiJuice2d's attackTexture selection so the
// Desktop preset shows the incoming attack's icon (Snohe for Pirate,
// Ghost for Seeker, Lodging for Vanguard) instead of always the
// Lodging glyph. ActionClockout's own default is the Lodging id, so
// the Vanguard value here is the same value it would otherwise use.
const PIRATE_ATTACK_TEXTURE_ID = 129;
const SEEKER_ATTACK_TEXTURE_ID = 76;
const VANGUARD_ATTACK_TEXTURE_ID = 72;

export function defendActionTextureId(card, currentAction) {
    if (PIRATES.includes(card)) return PIRATE_ATTACK_TEXTURE_ID;
    if (SEEKERS.includes(card)) return SEEKER_ATTACK_TEXTURE_ID;
    if (VANGUARDS.includes(card)) return VANGUARD_ATTACK_TEXTURE_ID;
    // Card-less opponent attack (Manual §8.2.2): no attacking guest
    // card, so the attack type rides on current_action (engine Action
    // ids GHOST=6 / LODGING=2). A Ghost attack queues ghosts (76); a
    // Lodging attack removes permits (72). Mirrors TwoD AntiJuice2d's
    // attackTexture branch — the old card==null fall-through always
    // returned the Lodging glyph, so a Ghost attack showed lodging
    // tokens in the defend swiper.
    return currentAction === 2
        ? VANGUARD_ATTACK_TEXTURE_ID
        : SEEKER_ATTACK_TEXTURE_ID;
}

function computeMode(gamePlayerData) {
    if (!gamePlayerData) return null;
    const status = gamePlayerData.status;
    const actionStatus = gamePlayerData.action_status;
    if (status === GamePlayerStatus.LODGING_ID
        && actionStatus === ActionStatus.WAIT_FOR_TARGET_ID) {
        // Manual §7.2.2: target announced BEFORE the Fate Die. The
        // target picker is the only surface visible at this status —
        // no clockout, no choice, no juice swiper.
        return 'target';
    }
    if (status === GamePlayerStatus.LODGING_ID
        && actionStatus === ActionStatus.WAIT_FOR_JUICE) {
        return 'juice';
    }
    if (status === GamePlayerStatus.LODGING_ID
        && actionStatus === ActionStatus.ADD_ID) {
        return 'add';
    }
    if (status === GamePlayerStatus.DEFENDING_ID) {
        // Anti-juice defence is identical for all three attacking
        // factions — clock out staff to buy Fate, then clickAntiJuice
        // (onCommit is faction-agnostic). The engine sets DEFENDING_ID
        // with a current_attacking_card for a Vanguard, Pirate, OR
        // Seeker (engine/round_choices/unsatisfied_guest.defend). The
        // old VANGUARDS-only gate dropped the window for Seeker/Pirate
        // attacks, so the human got NO defence affordance and the seat
        // froze — G147, an unsatisfied Dread Seeker: "not seeing staff
        // to fate for defence." Mirror TwoD AntiJuice2d, which already
        // renders for all three.
        const card = gamePlayerData.current_attacking_card;
        if (VANGUARDS.includes(card)
                || PIRATES.includes(card)
                || SEEKERS.includes(card)) {
            return 'defend';
        }
        // Opponent Ghost/Lodging attack defense (Manual §8.2.2). No attacking
        // guest card — current_attacking_card is null and the attack type
        // rides on current_action (engine Action ids: GHOST=6, LODGING=2).
        // Same clock-out defense affordance; render it, else the desktop
        // defender gets NO window and the seat freezes — the card-less twin
        // of the G147 Seeker gap fixed just above.
        if (card == null
                && (gamePlayerData.current_action === 6
                    || gamePlayerData.current_action === 2)) {
            return 'defend';
        }
    }
    return null;
}

// LodgingActionWindow — Lodging HUD for the Desktop preset. The
// FloatingWindow shell is owned by Dashboard.jsx. ActionInventory
// is always rendered; ActionClockout / ActionChoice / ActionAttack
// appear and disappear based on the computed mode. Each primitive
// owns its own per-row overlay artwork — the window does NOT pass
// overlay paths or render any wrapper-level overlay.
//
// soldRobots state is owned here. The state resets to [] whenever
// (status, action_status) transitions so a fresh juice / defense
// phase starts clean. Click handlers route through
// app.game.seatedController.gamePlayerApi.
export function LodgingActionWindow(props) {
    const { gamePlayerData, gameData, app } = props;

    const status = gamePlayerData ? gamePlayerData.status : null;
    const actionStatus = gamePlayerData
        ? gamePlayerData.action_status : null;

    const [soldRobots, setSoldRobots] = useState([]);

    useEffect(() => {
        setSoldRobots([]);
    }, [status, actionStatus]);

    const mode = computeMode(gamePlayerData);

    // Defend banner: name the attacking guest and the live incoming
    // count so a defender who refreshed mid-attack (losing the activity
    // transcript) can still see what is hitting them and how many
    // events remain. The count mirrors ActionClockout's currentFate —
    // each clocked-out staff buys back two events — so it ticks down as
    // the player clocks out, reaching 0 when the attack is fully bought
    // off. current_attacking_card is a plain card id (see
    // GamePlayerSerializer); engine sets it on every DEFENDING_ID seat.
    const defendCardName = (mode === 'defend' && gamePlayerData)
        ? (gamePlayerData.current_attacking_card != null
            ? CARD_NAMES[gamePlayerData.current_attacking_card]
            // Opponent Ghost/Lodging attack: no card — label by current_action
            // (engine Action ids GHOST=6 / LODGING=2) instead of CARD_NAMES[null].
            : (gamePlayerData.current_action === 2
                ? 'Lodging attack' : 'Ghost attack'))
        : null;
    const defendStartFate = (gamePlayerData
        && typeof gamePlayerData.current_fate === 'number')
        ? gamePlayerData.current_fate : 0;
    const defendRemaining = Math.max(
        0, defendStartFate - soldRobots.length * 2);

    // Manual §7.2.2: target was committed BEFORE the Fate Die. Self-
    // target → Supply (add/upgrade own lodging); opponent-target →
    // attack (engine routes _dispatch_spend → apply_attack). In juice
    // mode, the Supply affordances (pool selector, upgrade highlights)
    // are only valid when target = self. For opp-target the player
    // commits via the Confirm button in ActionClockout and the engine
    // runs apply_attack inline.
    const isSelfTarget = (
        !gamePlayerData
        || gamePlayerData.current_action_target == null
        || gamePlayerData.current_action_target === gamePlayerData.id);

    // Manual §3.2 Initial Lodging Round — this seat owes its
    // second-Lodging pick. The picker is its own component
    // (InitialLodgingChoice) already wired to
    // gamePlayerApi.initialLodgingChoice; we just include it here so
    // it surfaces inside the Lodging window instead of only via
    // ManualPlayerControls in the "Your Hotel" floating window.
    const isMyInitialLodgingTurn = !!(
        gamePlayerData && gameData
        && gamePlayerData.status === GamePlayerStatus.GAME_SETUP_ID
        && gameData.current_player === gamePlayerData.id);

    const api = (app && app.game && app.game.seatedController
        && app.game.seatedController.gamePlayerApi)
        ? app.game.seatedController.gamePlayerApi : null;

    const onClockOut = useCallback((robot) => {
        setSoldRobots((prev) => [...prev, robot]);
    }, []);

    const onReset = useCallback(() => {
        setSoldRobots([]);
    }, []);

    const onCommit = useCallback((soldList) => {
        if (!api) return;
        if (mode === 'juice') {
            api.lodgingJuice(soldList);
            return;
        }
        if (mode === 'defend') {
            api.clickAntiJuice(soldList);
        }
    }, [api, mode]);

    const onPoolClick = useCallback((factionId) => {
        if (!api || !gamePlayerData) return;
        // Source pattern (LodgingActionJuice.onLodgingPoolClick +
        // AddLodging2d.onLodgingPoolClick): clear the highlight
        // before firing the API. The double-dispatch with the
        // useEffect cleanup below is benign — the listener
        // idempotently clears its state.
        window.dispatchEvent(
            new Event('clear-lodging-highlight-event'));
        if (mode === 'juice') {
            api.lodgingFullAction(
                soldRobots, gamePlayerData.id, factionId, null);
            return;
        }
        if (mode === 'add') {
            api.addLodging(factionId);
        }
    }, [api, gamePlayerData, mode, soldRobots]);

    const onUpgradeClick = useCallback((lodgingGridIndex) => {
        if (!api || !gamePlayerData) return;
        // Source pattern (LodgingActionJuice.onLodgingUpgradeClick +
        // AddLodging2d.onClick): clear before API.
        window.dispatchEvent(
            new Event('clear-lodging-highlight-event'));
        if (mode === 'juice') {
            api.lodgingFullAction(
                soldRobots, gamePlayerData.id, null, lodgingGridIndex);
            return;
        }
        if (mode === 'add') {
            api.lodgingUpgrade(lodgingGridIndex);
        }
    }, [api, gamePlayerData, mode, soldRobots]);

    const onTargetClick = useCallback((playerId) => {
        if (!api) return;
        // Manual §7.2.2 — pre-roll target commit. Engine
        // (commit_target) persists current_action_target and runs the
        // Fate roll inline. No staff payload here: clockout / juice
        // are a separate phase post-roll.
        window.dispatchEvent(
            new Event('clear-lodging-highlight-event'));
        if (mode === 'target') {
            api.lodgingCommitTarget(playerId);
        }
    }, [api, mode]);

    // Highlight dispatch lives here because the source's
    // `showLodgingUpgrades` (LodgingActionJuice) fires whenever
    // soldRobots changes — a state that, in this port, is owned by
    // LodgingActionWindow. Juice mode gates on derived currentFate
    // > 0 (matches the source's `if (currentFate === 0)
    // clearLodgingUpgrades` guard). Add mode dispatches whenever
    // upgradables are present, matching
    // AddLodging2d.componentDidUpdate which fires on every props
    // change. Cleanup fires clear, so transitioning out of
    // juice/add (or unmounting the window) leaves the listener
    // clean.
    useEffect(() => {
        if (mode !== 'juice' && mode !== 'add') return undefined;
        if (!gamePlayerData
            || !Array.isArray(gamePlayerData.lodging)) return undefined;
        // Upgrade highlight is a Supply affordance. mode='add' is
        // implicitly self-target (engine only sets ADD_ID for self).
        // mode='juice' is target-agnostic — gate on isSelfTarget so we
        // don't invite a Supply click during an opp attack.
        if (mode === 'juice' && !isSelfTarget) return undefined;
        const upgradables = unUpgradedLodgingIndexes(gamePlayerData);
        if (!upgradables.length) return undefined;
        if (mode === 'juice') {
            const startingFate = gamePlayerData.current_fate || 0;
            const derivedFate = startingFate + (soldRobots.length * 2);
            if (derivedFate === 0) return undefined;
        }
        window.dispatchEvent(new CustomEvent(
            'highlight-lodging-event', {
                detail: {
                    indexes: upgradables,
                    callback: onUpgradeClick,
                },
            }));
        return () => {
            window.dispatchEvent(
                new Event('clear-lodging-highlight-event'));
        };
    }, [mode, gamePlayerData, soldRobots, onUpgradeClick, isSelfTarget]);

    // Additive second input path for the Manual §7.2.2 target-pick
    // step: light up the eligible competitor boards inside the
    // Competitors floating window. Mirrors
    // AdvertisementController.handleOrbitClick's
    // highlight-competitors-event dispatch (and the mobile
    // GhostActionTarget's). The ActionAttack swiper above keeps
    // working untouched — this is a second affordance on the same
    // step, not a replacement. Candidate set is the SAME picker the
    // swiper uses (playersWithAttackableLodging: self when it can
    // Supply, plus attackable opponents). Bail without dispatching
    // when the set is empty, matching AdvertisementController's
    // `possibleTargets.length > 0` guard.
    useEffect(() => {
        if (mode !== 'target') return undefined;
        if (!gameData || !gamePlayerData) return undefined;
        const candidates = playersWithAttackableLodging(
            gameData, gamePlayerData);
        const playerIds = candidates.map((gp) => gp.id);
        if (playerIds.length === 0) return undefined;
        window.dispatchEvent(new CustomEvent(
            'highlight-competitors-event', {
                detail: {
                    playerIds: playerIds,
                    callback: onTargetClick,
                },
            }));
        return () => {
            window.dispatchEvent(
                new Event('clear-competitors-highlight-event'));
        };
    }, [mode, gameData, gamePlayerData, onTargetClick]);

    const wrapperClasses = [
        'lodging-action-window',
        mode ? 'lodging-action-window--' + mode
             : 'lodging-action-window--idle',
    ].join(' ');

    return (
        <div className={wrapperClasses}
             data-testid={'lodging-action-window'}
             data-mode={mode || 'idle'}>
            <ActionWrapper>
                <ActionInventory gamePlayerData={gamePlayerData}/>
                {isMyInitialLodgingTurn && (
                    <InitialLodgingChoice
                        app={app}
                        gameData={gameData}/>
                )}
                {mode === 'defend' && (
                    <div className={'lodging-action-defend-banner'}
                         data-testid={'defend-banner'}>
                        <span className={
                                'lodging-action-defend-banner-card'}>
                            {defendCardName || 'Unsatisfied Guest'}
                        </span>
                        <span className={
                                'lodging-action-defend-banner-count'}>
                            {defendRemaining}
                            {defendRemaining === 1
                                ? ' attack' : ' attacks'}
                        </span>
                    </div>
                )}
                {(mode === 'juice' || mode === 'defend') && (
                    <ActionClockout
                        gamePlayerData={gamePlayerData}
                        mode={mode}
                        soldRobots={soldRobots}
                        isSelfTarget={isSelfTarget}
                        actionTextureId={mode === 'defend'
                            ? defendActionTextureId(
                                gamePlayerData
                                    && gamePlayerData.current_attacking_card,
                                gamePlayerData
                                    && gamePlayerData.current_action)
                            : undefined}
                        onClockOut={onClockOut}
                        onReset={onReset}
                        onCommit={onCommit}/>
                )}
                {/* Pool selector is the Supply affordance. mode='add'
                    is implicitly self-target. mode='juice' is target-
                    agnostic — hide when target = opp so the player
                    doesn't think they're being asked to add when the
                    engine is actually waiting to attack. */}
                {((mode === 'juice' && isSelfTarget) || mode === 'add') && (
                    <ActionChoice
                        gamePlayerData={gamePlayerData}
                        gameData={gameData}
                        mode={mode}
                        onPoolClick={onPoolClick}/>
                )}
                {mode === 'target' && (
                    <ActionAttack
                        gamePlayerData={gamePlayerData}
                        gameData={gameData}
                        onTargetClick={onTargetClick}/>
                )}
            </ActionWrapper>
        </div>
    );
}

export default LodgingActionWindow;
